import { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import { useBusinessAdmin } from '../../contexts/BusinessAdminContext';
import { Agent } from '../../types/business';

interface AgentActionsDropdownProps {
  agent: Agent;
  onEdit: (agent: Agent) => void;
}

const AgentActionsDropdown: React.FC<AgentActionsDropdownProps> = ({ agent, onEdit }) => {
  const { deleteAgent, isDeletingAgent } = useBusinessAdmin();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewProfileClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    // Navigate to the agent's profile page
    window.open(`/agent/${encodeURIComponent(agent.email)}`, '_blank');
    handleMenuClose();
  };

  const handleEditClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onEdit(agent);
    handleMenuClose();
  };

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    setError(null);
    const result = await deleteAgent(agent.user.email);
    
    if (result.success) {
      setDeleteDialogOpen(false);
    } else {
      setError(result.error || 'Failed to delete agent');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setError(null);
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleMenuOpen}
        disabled={isDeletingAgent}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleViewProfileClick}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="View Profile" />
        </MenuItem>
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Edit Agent" />
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary="Delete Agent" />
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={!isDeletingAgent ? handleDeleteCancel : undefined}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Agent</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{agent.name}</strong> ({agent.user.email}) 
            from your business? This action cannot be undone.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1, fontWeight: 'medium', color: 'warning.main' }}>
            Note: This will only remove them as an agent from your business. 
            Their user account will remain active.
          </DialogContentText>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDeleteCancel}
            disabled={isDeletingAgent}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeletingAgent}
            startIcon={isDeletingAgent ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {isDeletingAgent ? 'Deleting...' : 'Delete Agent'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AgentActionsDropdown;